import i18n from '../i18n';
import logger from './logger';

/**
 * Language Integrity Service
 * 
 * Runtime check that verifies the currently loaded content matches
 * the user's selected language. If contamination is detected
 * (e.g. German fallback in an Urdu session), it auto-triggers a
 * data refresh to fix the issue.
 * 
 * This prevents "Mischbetrieb" where the user sees a mix of languages.
 */

// German marker phrases that should NOT appear in non-German contexts
const GERMAN_MARKERS = [
  'Vorfahrt', 'Geschwindigkeit', 'Fahrzeug', 'Straße', 'Kreuzung',
  'Überholen', 'Abbiegen', 'Führerschein', 'Bremsen', 'Rückspiegel',
  'Schutzweg', 'Autobahn', 'Fahrstreifen', 'Gegenverkehr', 'Vorrang',
  'Halten und Parken', 'Ladezone', 'Ampel', 'Zebrastreifen',
];

// Quick detection: does a text look like German?
function looksGerman(text: string): boolean {
  if (!text || text.length < 10) return false;
  const lower = text.toLowerCase();
  return GERMAN_MARKERS.some(m => lower.includes(m.toLowerCase()));
}

// Quick detection: is the text in a non-Latin script? (Arabic, Urdu, Dari)
function isNonLatinScript(text: string): boolean {
  // Check for Arabic/Urdu/Dari characters (Unicode range)
  return /[\u0600-\u06FF\u0750-\u077F\uFB50-\uFDCF\uFE70-\uFEFF]/.test(text);
}

export interface IntegrityReport {
  language: string;
  totalChecked: number;
  contaminated: number;
  contaminatedIds: string[];
  isClean: boolean;
  details: string;
}

/**
 * Check if questions are correctly localized for the active language.
 * Returns an integrity report. If contamination is found, the caller
 * can trigger a force-refresh.
 */
export function checkQuestionIntegrity(
  questions: Array<{ id: string; questionText: string }>,
  activeLanguage?: string
): IntegrityReport {
  const lang = activeLanguage || i18n.language || 'de';
  
  // Skip check for German — it's the source language, never contaminated
  if (lang === 'de') {
    return {
      language: lang,
      totalChecked: questions.length,
      contaminated: 0,
      contaminatedIds: [],
      isClean: true,
      details: 'German is the source language — no contamination possible.',
    };
  }

  const contaminatedIds: string[] = [];
  const isRTL = ['ar', 'prs', 'ur'].includes(lang);

  for (const q of questions) {
    if (!q.questionText) continue;
    
    // For RTL languages: if the question text has NO Arabic/Dari/Urdu script,
    // it's almost certainly a German fallback
    if (isRTL && !isNonLatinScript(q.questionText) && q.questionText.length > 20) {
      contaminatedIds.push(q.id);
      continue;
    }

    // For all non-German languages: if the text contains typical German
    // driving theory phrases, it's a fallback
    if (!isRTL && looksGerman(q.questionText)) {
      contaminatedIds.push(q.id);
    }
  }

  const contaminated = contaminatedIds.length;
  const isClean = contaminated === 0;

  return {
    language: lang,
    totalChecked: questions.length,
    contaminated,
    contaminatedIds: contaminatedIds.slice(0, 20), // Cap for performance
    isClean,
    details: isClean
      ? `All ${questions.length} questions are correctly in ${lang}.`
      : `${contaminated}/${questions.length} questions appear to be German fallbacks in ${lang} mode.`,
  };
}

/**
 * Check if the i18n UI translations are clean for the active language.
 * Detects when too many UI strings are German (indicates incomplete locale).
 */
export function checkUIIntegrity(activeLanguage?: string): IntegrityReport {
  const lang = activeLanguage || i18n.language || 'de';

  if (lang === 'de') {
    return {
      language: lang,
      totalChecked: 0,
      contaminated: 0,
      contaminatedIds: [],
      isClean: true,
      details: 'German is the source language.',
    };
  }

  // Sample key UI strings that must be translated
  const criticalKeys = [
    'Lernen', 'Prüfung', 'Profil', 'Einstellungen',
    'Anmelden', 'Abmelden', 'Weiter', 'Zurück', 'Abbrechen',
  ];

  const contaminatedKeys: string[] = [];
  
  for (const key of criticalKeys) {
    const translated = i18n.t(key);
    // If the translation is identical to the key AND the key is German,
    // it means fallback happened
    if (translated === key) {
      contaminatedKeys.push(key);
    }
  }

  // Also check: for RTL languages, if the resolved translations
  // are still in Latin script, something is wrong
  const isRTL = ['ar', 'prs', 'ur'].includes(lang);
  if (isRTL) {
    const sampleTranslations = criticalKeys.map(k => i18n.t(k));
    const nonRTLCount = sampleTranslations.filter(t => t.length > 3 && !isNonLatinScript(t)).length;
    if (nonRTLCount > criticalKeys.length / 2) {
      return {
        language: lang,
        totalChecked: criticalKeys.length,
        contaminated: nonRTLCount,
        contaminatedIds: criticalKeys,
        isClean: false,
        details: `RTL language ${lang} has ${nonRTLCount}/${criticalKeys.length} UI strings in Latin script — likely wrong locale loaded.`,
      };
    }
  }

  return {
    language: lang,
    totalChecked: criticalKeys.length,
    contaminated: contaminatedKeys.length,
    contaminatedIds: contaminatedKeys,
    isClean: contaminatedKeys.length === 0,
    details: contaminatedKeys.length === 0
      ? `UI translations clean for ${lang}.`
      : `${contaminatedKeys.length} critical UI keys are untranslated in ${lang}: ${contaminatedKeys.join(', ')}`,
  };
}

/**
 * Run all integrity checks and auto-fix if contamination is detected.
 * Call this after loading questions or changing language.
 * 
 * @returns true if everything is clean, false if auto-fix was triggered
 */
export async function runIntegrityCheckAndAutoFix(
  questions: Array<{ id: string; questionText: string }>,
  onForceRefresh: (lang: string) => Promise<void>,
  activeLanguage?: string
): Promise<boolean> {
  const lang = activeLanguage || i18n.language || 'de';
  if (lang === 'de') return true;

  const questionReport = checkQuestionIntegrity(questions, lang);
  const uiReport = checkUIIntegrity(lang);

  if (questionReport.isClean && uiReport.isClean) {
    logger.info('languageIntegrity', `[Integrity] ✅ ${lang}: All content correctly localized`);
    return true;
  }

  // Contamination detected — auto-fix
  if (!questionReport.isClean) {
    logger.warn('Integrity',
      `⚠️ ${lang}: ${questionReport.contaminated} questions contaminated — forcing re-fetch...`
    );
  }
  if (!uiReport.isClean) {
    logger.warn('Integrity', `⚠️ ${lang}: UI contamination — ${uiReport.details}`);
    // Force i18n to reload
    await i18n.changeLanguage(lang);
  }

  // Trigger question force-refresh
  if (!questionReport.isClean) {
    try {
      await onForceRefresh(lang);
      logger.info('languageIntegrity', `[Integrity] 🔄 ${lang}: Auto-refresh completed`);
    } catch (err) {
      logger.error('Integrity', `❌ ${lang}: Auto-refresh failed:`, err);
    }
  }

  return false;
}
